// Iter 83.4 / Phase E4 Sprint 4 — FNF Statement Print page.
// Employee-facing settlement statement (signed copy). Pulled into
// Sprint 4 per operator directive. Renders the full FNF breakdown:
// earnings · gratuity · leave encashment · TDS reconcile · recoveries
// · net payable · SHA-256 freeze certificate banner.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const fmtMoney = (n) =>
    `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function FnfStatementPrint() {
    const { fnfId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data: d } = await api.get(`/fnf/${fnfId}/statement-payload`);
                setData(d);
            } catch (err) {
                setError(formatApiError(err, "Failed to load FNF statement"));
                toast.error(formatApiError(err, "Load failed"));
            }
        })();
    }, [fnfId]);

    if (error) return <div className="p-12 text-center text-rose-600" data-testid="fnf-stmt-error">{error}</div>;
    if (!data) return <div className="p-12 text-center text-slate-400">Loading…</div>;

    const { fnf, exit_event: exitEvent, employee, freeze_certificate: cert, outstanding_recovery, company, generated_at } = data;
    const prorataRows = fnf.pro_rata_earnings || [];
    const grat = fnf.gratuity || {};
    const enc = fnf.leave_encashment || {};
    const notice = fnf.notice_recovery || {};
    const loan = fnf.loan_recovery || {};
    const asset = fnf.asset_recovery || {};
    const tdsRecon = fnf.fytd_tds_reconciliation || {};

    return (
        <div className="bg-white min-h-screen p-12 font-serif" data-testid="fnf-statement-print">
            <style>{`@media print { @page { size: A4; margin: 12mm; } body { -webkit-print-color-adjust: exact; } }`}</style>

            {/* Header */}
            <header className="border-b-4 border-slate-900 pb-4 mb-6">
                <div className="flex justify-between items-start">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-600">{company.legal_name || "REEDOS"}</div>
                        <h1 className="text-3xl font-bold mt-1">Full & Final Settlement Statement</h1>
                        <div className="text-sm text-slate-600 mt-1">
                            Iter 83.4 / Sprint 4 · Employee Sign-off Copy
                        </div>
                    </div>
                    <div className="text-right text-xs">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Generated</div>
                        <div className="font-mono">{(generated_at || "").slice(0, 19).replace("T", " ")}</div>
                    </div>
                </div>
            </header>

            {/* Employee + Exit context */}
            <section className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm mb-6">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Employee</div>
                    <div className="font-semibold">{fnf.employee_name_snapshot || (employee?.full_name)}</div>
                    <div className="text-xs text-slate-600">{employee?.employee_code} · {employee?.designation_name_snapshot || employee?.designation}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Exit</div>
                    <div>{fnf.exit_type} · LWD {fnf.lwd}</div>
                    {exitEvent?.notice_period_days_required != null && (
                        <div className="text-xs text-slate-600">
                            Notice required: {exitEvent.notice_period_days_required} d · served: {exitEvent.notice_period_days_served} d
                        </div>
                    )}
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">FNF ID</div>
                    <div className="font-mono">{fnf.id}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Status</div>
                    <div className="font-semibold">
                        {fnf.status}
                        {fnf.is_negative && <span className="ml-2 text-rose-700 text-xs">(D23 NEGATIVE FNF)</span>}
                    </div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Date of Joining</div>
                    <div>{employee?.date_of_joining || "—"}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Calculated</div>
                    <div>{(fnf.calculated_at || "").slice(0, 19).replace("T", " ")}</div>
                </div>
            </section>

            {/* Earnings */}
            <section className="border-t border-slate-300 pt-4 mb-6">
                <h2 className="text-lg font-bold mb-2">Earnings</h2>
                <table className="w-full text-sm">
                    <tbody>
                        {prorataRows.length > 0 && (
                            <>
                                <tr className="border-b border-slate-200 bg-slate-50">
                                    <td colSpan={2} className="py-1.5 px-2 text-xs font-semibold uppercase text-slate-600">Pro-rata earnings ({prorataRows.length} component{prorataRows.length === 1 ? "" : "s"})</td>
                                </tr>
                                {prorataRows.map((r, i) => (
                                    <tr key={i} className="border-b border-slate-100">
                                        <td className="py-1 px-2 text-slate-700 text-xs">{r.component_code || r.code} · {r.component_name || r.name || ""}</td>
                                        <td className="py-1 px-2 text-right font-mono text-xs">{fmtMoney(r.pro_rata_amount)}</td>
                                    </tr>
                                ))}
                                <tr className="border-b-2 border-slate-300">
                                    <td className="py-1 px-2 text-slate-800 font-semibold text-xs">Subtotal · Pro-rata</td>
                                    <td className="py-1 px-2 text-right font-mono font-semibold">{fmtMoney(fnf.pro_rata_total)}</td>
                                </tr>
                            </>
                        )}
                        <tr className="border-b border-slate-100">
                            <td className="py-1 px-2">Gratuity (D21 · {grat.formula_used || "n/a"})</td>
                            <td className="py-1 px-2 text-right font-mono">{fmtMoney(grat.amount)}</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                            <td className="py-1 px-2">Leave Encashment (D22 · Basic + DA · {enc.encashable_days || 0} days)</td>
                            <td className="py-1 px-2 text-right font-mono">{fmtMoney(enc.amount)}</td>
                        </tr>
                        {Number(tdsRecon.refund_added_to_earnings || 0) > 0 && (
                            <tr className="border-b border-slate-100">
                                <td className="py-1 px-2">FYTD TDS Refund</td>
                                <td className="py-1 px-2 text-right font-mono">{fmtMoney(tdsRecon.refund_added_to_earnings)}</td>
                            </tr>
                        )}
                        <tr className="border-t-2 border-slate-900 bg-emerald-50">
                            <td className="py-2 px-2 font-bold">Total Earnings</td>
                            <td className="py-2 px-2 text-right font-mono font-bold text-base">{fmtMoney(fnf.earnings_subtotal)}</td>
                        </tr>
                    </tbody>
                </table>
            </section>

            {/* Deductions */}
            <section className="border-t border-slate-300 pt-4 mb-6">
                <h2 className="text-lg font-bold mb-2">Deductions & Recoveries</h2>
                <table className="w-full text-sm">
                    <tbody>
                        {Number(notice.amount || 0) > 0 && (
                            <tr className="border-b border-slate-100">
                                <td className="py-1 px-2">Notice Period Recovery · {notice.mode} ({notice.effective_short_days || 0} days)</td>
                                <td className="py-1 px-2 text-right font-mono">{fmtMoney(notice.amount)}</td>
                            </tr>
                        )}
                        {Number(loan.amount || 0) > 0 && (
                            <tr className="border-b border-slate-100">
                                <td className="py-1 px-2">
                                    Loan Recovery (D16 · {(loan.breakdown || []).length} item)
                                    {(loan.breakdown || []).map((b, i) => (
                                        <div key={i} className="text-[10px] text-slate-500 pl-3">— {b.loan_id} · {b.policy_code} · outstanding {fmtMoney(b.outstanding)}</div>
                                    ))}
                                </td>
                                <td className="py-1 px-2 text-right font-mono align-top">{fmtMoney(loan.amount)}</td>
                            </tr>
                        )}
                        {Number(asset.amount || 0) > 0 && (
                            <tr className="border-b border-slate-100">
                                <td className="py-1 px-2">
                                    Asset Recovery (D18 · D26 · {(asset.breakdown || []).length} item)
                                    {(asset.breakdown || []).map((b, i) => (
                                        <div key={i} className="text-[10px] text-slate-500 pl-3">— {b.asset_tag} · {b.outcome} · {fmtMoney(b.amount)}</div>
                                    ))}
                                </td>
                                <td className="py-1 px-2 text-right font-mono align-top">{fmtMoney(asset.amount)}</td>
                            </tr>
                        )}
                        {Number(tdsRecon.extra_deduction || 0) > 0 && (
                            <tr className="border-b border-slate-100">
                                <td className="py-1 px-2">FYTD TDS Reconciliation (extra deduction)</td>
                                <td className="py-1 px-2 text-right font-mono">{fmtMoney(tdsRecon.extra_deduction)}</td>
                            </tr>
                        )}
                        <tr className="border-t-2 border-slate-900 bg-rose-50">
                            <td className="py-2 px-2 font-bold">Total Deductions</td>
                            <td className="py-2 px-2 text-right font-mono font-bold text-base">{fmtMoney(fnf.deductions_subtotal)}</td>
                        </tr>
                    </tbody>
                </table>
            </section>

            {/* Net */}
            <section className="border-t-4 border-slate-900 pt-4 mb-6">
                <table className="w-full text-base">
                    <tbody>
                        <tr>
                            <td className="py-2 px-2 font-bold uppercase tracking-wide">Net Settlement Amount</td>
                            <td className={`py-2 px-2 text-right font-mono font-bold text-2xl ${fnf.is_negative ? "text-rose-700" : "text-emerald-700"}`}>
                                {fmtMoney(fnf.net_settlement_amount)}
                            </td>
                        </tr>
                        {fnf.is_negative && (
                            <tr>
                                <td colSpan={2} className="text-xs px-2 py-1 bg-rose-50 border border-rose-300 rounded mt-1">
                                    <div className="font-semibold text-rose-800">D23 Negative FNF — Outstanding Recovery</div>
                                    <div className="text-rose-700">Shortfall: {fmtMoney(fnf.shortfall_amount)}</div>
                                    {outstanding_recovery && (
                                        <div className="text-rose-700">Record ID: <span className="font-mono">{outstanding_recovery.id}</span> · Status: {outstanding_recovery.status}</div>
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </section>

            {/* Approval Chain */}
            {(fnf.approval_chain || []).length > 0 && (
                <section className="border-t border-slate-300 pt-4 mb-6">
                    <h2 className="text-sm font-bold mb-2 uppercase tracking-wide text-slate-600">D24 4-eye Approval Chain</h2>
                    {fnf.approval_chain.map((a, i) => (
                        <div key={i} className="text-xs mb-1">
                            <span className="font-semibold">{a.approver_name || a.approver_email}</span>
                            {a.approver_email && <span className="text-slate-500"> ({a.approver_email})</span>}
                            <span className="text-slate-500 ml-2">at {a.approved_at}</span>
                            {a.note && <div className="italic text-slate-600">"{a.note}"</div>}
                        </div>
                    ))}
                </section>
            )}

            {/* Acknowledgement */}
            <section className="border-t border-slate-300 pt-6 mb-6">
                <h2 className="text-sm font-bold mb-3 uppercase tracking-wide text-slate-600">Employee Acknowledgement</h2>
                <p className="text-xs text-slate-700 mb-4 leading-relaxed">
                    I, <strong>{fnf.employee_name_snapshot}</strong>, hereby acknowledge that I have received the
                    final settlement amount of <strong>{fmtMoney(fnf.net_settlement_amount)}</strong> in full and
                    final settlement of all dues, claims, and demands of whatsoever nature against the Company
                    arising out of or in connection with my employment. I confirm that I have read and understood
                    the breakdown above. I will return all Company property, confidential materials, and access
                    devices, and acknowledge that this statement supersedes all prior communications regarding my
                    settlement.
                </p>
                <div className="grid grid-cols-2 gap-12 pt-8">
                    <div>
                        <div className="border-t border-slate-700 pt-1 text-xs text-slate-700">
                            Employee Signature · Date
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1">{fnf.employee_name_snapshot}</div>
                    </div>
                    <div>
                        <div className="border-t border-slate-700 pt-1 text-xs text-slate-700">
                            Authorised Signatory · Date
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1">For {company.legal_name || "REEDOS"}</div>
                    </div>
                </div>
            </section>

            {/* Freeze certificate banner */}
            {cert && (
                <footer className="border-t-2 border-slate-900 pt-3 text-xs">
                    <div className="font-semibold uppercase tracking-widest text-slate-700">D28 Freeze Certificate Reference</div>
                    <div className="mt-1">Certificate ID: <span className="font-mono">{cert.id}</span></div>
                    <div className="font-mono mt-1 break-all bg-slate-100 p-2 rounded text-[10px]">SHA-256: {cert.hash}</div>
                    <div className="text-slate-500 mt-1">
                        This settlement is cryptographically sealed at approval. The Freeze Certificate is available
                        for independent re-verification at any time.
                    </div>
                </footer>
            )}
        </div>
    );
}
